import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { PhoneFilled } from 'src/shared/components/icons/phone-filled'
import { Button } from 'src/shared/components/ui/buttons/button'
import Portal from 'src/shared/components/utils/portals/portal'
import { useAuthState } from 'src/shared/features/auth/api/use-auth-state'
import ConsumerAuthDialog from 'src/shared/features/auth/components/consumer/auth-dialog'
import { useCallLead } from 'src/shared/features/consumers/api/use-call-lead'
import { ConversionAction, useConversionTracking } from 'src/shared/hooks/use-conversion-tracking'
import { useIsMobile } from 'src/shared/hooks/use-mobile'
import { useToast } from 'src/shared/hooks/use-toast'
import { cn } from 'src/shared/utils/cn'

import { useUserByUsername } from '../../../api/accounts/use-user-by-username'
import { useUserFeaturePreferences } from '../../../api/preferences/use-user-feature-preferences'
import { RequestCallPhone } from '../../../assets/request-call-phone'

type RequestCallButtonProps = {
  username: string
  className?: string
  textClassName?: string
  text?: string
  type?: 'default' | 'icon'
  showPhoneIcon?: boolean
}

const RequestCallButton = ({
  username,
  className,
  textClassName,
  text = 'Request call now',
  type = 'default',
  showPhoneIcon = true,
}: RequestCallButtonProps) => {
  const { data: user } = useUserByUsername(username)
  const { isConsumerSignedIn } = useAuthState()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const [authDialogOpen, setAuthDialogOpen] = useState(false)
  const { mutate: callLead, isPending } = useCallLead()
  const { toast } = useToast()
  const { trackConversion } = useConversionTracking()
  const { data: userFeaturePreferences } = useUserFeaturePreferences(user?.id || -1)

  const leadCallMethod = userFeaturePreferences?.leadCallMethod || 'lynk'

  const onRequestCallClick = (): void => {
    if (!user) return

    if (leadCallMethod === 'calendly') {
      window.open(user?.calendlyUrl || '', '_blank')
      return
    } else if (leadCallMethod === 'user_phone') {
      window.open(`tel:${user?.phoneNumber || ''}`, '_blank')
      return
    }

    if (isConsumerSignedIn) {
      callLead(
        { receiverId: user?.id },
        {
          onSuccess: () => {
            // Track Google Ads conversion for call request
            trackConversion(ConversionAction.REQUEST_CALL)

            toast({
              title: 'Call request sent successfully',
              description: `Your call request has been sent to ${user?.firstName}`,
              variant: 'default',
            })
          },
          onError: (error) => {
            toast({
              title: 'Failed to send call request',
              description: 'Please try again later',
              variant: 'destructive',
            })
            console.error('Call request error:', error)
          },
        },
      )
    } else if (isMobile) {
      const currentPath = window.location.pathname
      navigate(paths.consumer.auth.signup.getHref(currentPath))
    } else {
      // Always show the dialog when button is clicked and user is not signed in
      setAuthDialogOpen(true)
      return
    }
  }

  return (
    <>
      {type === 'icon' ? (
        <Button
          variant="default"
          className="size-[50px] shrink-0 rounded-full bg-[#F8F7F5] p-0"
          onClick={onRequestCallClick}
        >
          <PhoneFilled size={22} className="text-primary" />
        </Button>
      ) : (
        <Button
          disabled={isPending}
          onClick={onRequestCallClick}
          variant="default"
          size="default"
          className={cn(
            'min-h-8 gap-1 rounded-full border-[0.5px] border-secondaryInverse px-4 md:min-h-12 md:gap-2 md:px-6',
            className,
          )}
        >
          {showPhoneIcon && <RequestCallPhone className="mr-2 size-3.5 md:size-5" />}
          <p className={cn('text-xs font-semibold md:text-sm', textClassName)}>{text}</p>
        </Button>
      )}

      <Portal>
        <ConsumerAuthDialog
          open={authDialogOpen}
          onOpenChange={setAuthDialogOpen}
          initialView="signup"
          signupTitle="Before we connect you..."
        />
      </Portal>
    </>
  )
}

export default RequestCallButton
